<template>
<!-- Hero Section -->
    <section id="home" class="hero">
        <div class="container">
            <div class="hero-content">
                <div class="hero-text">
                    <div class="hero-label">Quality Engineering Leader</div>
                    <h1>Sumantha Chandrashekara</h1>
                    <p class="hero-tagline">
                        Method Developer | Test Architect | Co-Founder
                    </p>
                    <p class="hero-description">
                        Building excellence in quality engineering through systematic methodologies, functional safety compliance, and intelligent automation frameworks. Co-founder of QayiLabs and Mviaso Technologies, pioneering the future of structured testing.
                    </p>
                    <div class="hero-cta">
                        <a href="#contact" class="btn btn-primary">Get In Touch</a>
                        <a href="#ventures" class="btn btn-secondary">View Ventures</a>
                    </div>
                </div>
                <div class="hero-stats">
                    <div class="stat-grid">
                        <div class="stat-item">
                            <div class="stat-number">19+</div>
                            <div class="stat-label">Years of Leadership Experience</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-number">ISO</div>
                            <div class="stat-label">26262 & 21434 Compliance Expert</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-number">2</div>
                            <div class="stat-label">Co-Founded Ventures</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- About Section -->
    <section id="about">
        <div class="container">
            <div class="section-header">
                <div class="section-subtitle">Introduction</div>
                <h2>About Me</h2>
                <p class="section-description">
                    A quality engineering leader dedicated to transforming how organizations approach software testing and compliance
                </p>
            </div>
            <div class="about-content">
                <div class="about-text">
                    <p>
                        With <span class="highlight">19+ years of progressive experience</span>, I've evolved from hands-on testing to architecting quality frameworks that ensure safety, compliance, and reliability in complex automotive and embedded systems.
                    </p>
                    <p>
                        Currently serving as <span class="highlight">Method Developer at Scania Group</span> in Stockholm, I develop test strategies that ensure ISO-26262 functional safety and ISO-21434 cybersecurity compliance. My work spans creating frameworks for AD/ADAS systems, coaching organizations on quality transformation, and establishing processes that make compliance achievable without bureaucracy.
                    </p>
                    <p>
                        As <span class="highlight">Co-Founder of QayiLabs</span>, I lead a specialized QA consulting practice with a team of 10 engineers, delivering ROI-driven automation solutions. At <span class="highlight">Mviaso Technologies</span>, we're pioneering Formal Language Models (FLMs) for structured testing domains—building the next generation of intelligent, compliant testing tools.
                    </p>
                    <p>
                        I believe quality is strategic, not tactical. It's about designing systems that prevent defects, creating frameworks that scale, and building organizational capabilities that ensure long-term excellence.
                    </p>
                </div>
                <div class="about-features">
                    <h3>Core Focus Areas</h3>
                    <ul class="feature-list">
                        <li>
                            <div class="feature-icon">🏗️</div>
                            <span>Test Architecture & Strategy</span>
                        </li>
                        <li>
                            <div class="feature-icon">🚗</div>
                            <span>Automotive Safety & Compliance</span>
                        </li>
                        <li>
                            <div class="feature-icon">⚙️</div>
                            <span>Method Development</span>
                        </li>
                        <li>
                            <div class="feature-icon">🤖</div>
                            <span>Intelligent Automation</span>
                        </li>
                        <li>
                            <div class="feature-icon">👥</div>
                            <span>Team Leadership & Coaching</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </section>

    <!-- Ventures Section -->
    <section id="ventures" class="ventures-section">
        <div class="container">
            <div class="section-header">
                <div class="section-subtitle">Entrepreneurship</div>
                <h2>Co-Founded Ventures</h2>
                <p class="section-description">
                    Building innovative companies that transform quality engineering and testing practices
                </p>
            </div>
            <div class="ventures-grid">
                <div class="venture-card">
                    <div class="venture-header">
                        <div>
                            <div class="venture-logo">QayiLabs</div>
                            <div class="venture-role">Co-Founder & QA Lead</div>
                        </div>
                        <a href="https://qayilabs.com/" target="_blank" class="venture-link">Visit Site</a>
                    </div>
                    <p>
                        A specialized QA automation consulting practice helping companies achieve testing excellence through comprehensive solutions—from strategic planning to execution and optimization.
                    </p>
                    <p>
                        Leading a team of 10 experienced QA engineers with deep expertise in modular automation frameworks, DevOps integration, and compliance testing for regulated industries. We deliver measurable ROI through reduced maintenance overhead and accelerated release cycles.
                    </p>
                    <p>
                        <span class="highlight">Services:</span> Test strategy development, framework architecture, ISO compliance consulting, embedded systems testing, automotive QA, and team capability building.
                    </p>
                </div>
                <div class="venture-card">
                    <div class="venture-header">
                        <div>
                            <div class="venture-logo">Mviaso Technologies</div>
                            <div class="venture-role">Co-Founder & Test Architect</div>
                        </div>
                        <a href="https://mviaso.com/" target="_blank" class="venture-link">Visit Site</a>
                    </div>
                    <p>
                        A research-focused technology company pioneering Formal Language Models (FLMs) for structured domains that require absolute correctness and regulatory compliance.
                    </p>
                    <p>
                        Building next-generation domain-intelligent testing tools that combine formal methods' precision with modern engineering flexibility. Our product suite include domain tailored Agent-as-a-Service for enterprise automation platforms.
                    </p>
                    <p>
                        <span class="highlight">Innovation:</span> FLMs deliver 10.7x faster performance than LLM approaches for structured domains, with on-premise deployment ensuring GDPR, HIPAA, and SOC2 compliance.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- Expertise Section -->
    <section id="expertise">
        <div class="container">
            <div class="section-header">
                <div class="section-subtitle">Specializations</div>
                <h2>Areas of Expertise</h2>
            </div>
            <div class="expertise-grid">
                <div class="expertise-card">
                    <div class="expertise-icon">🏗️</div>
                    <h3>Test Architecture & Automation</h3>
                    <p>
                        Designing scalable test architectures and comprehensive automation strategies for complex systems. Building modular frameworks with minimal maintenance overhead that support CI/CD pipelines and enable continuous delivery.
                    </p>
                    <div class="expertise-tags">
                        <span class="tag">Framework Design</span>
                        <span class="tag">DevOps</span>
                        <span class="tag">CI/CD</span>
                        <span class="tag">Python/pytest</span>
                        <span class="tag">Selenium</span>
                    </div>
                </div>
                <div class="expertise-card">
                    <div class="expertise-icon">🚗</div>
                    <h3>Automotive & Functional Safety</h3>
                    <p>
                        Comprehensive expertise in automotive testing with specialized focus on AD/ADAS systems, functional safety compliance (ISO-26262), and cybersecurity standards (ISO-21434, R155, R156). Creating test methodologies for safety-critical systems.
                    </p>
                    <div class="expertise-tags">
                        <span class="tag">ISO-26262</span>
                        <span class="tag">ISO-21434</span>
                        <span class="tag">AD/ADAS</span>
                        <span class="tag">FuSa</span>
                        <span class="tag">Cybersecurity</span>
                    </div>
                </div>
                <div class="expertise-card">
                    <div class="expertise-icon">⚙️</div>
                    <h3>Method Development & Coaching</h3>
                    <p>
                        Developing test methods, creating comprehensive training programs, and coaching organizations on quality transformation. Establishing processes that ensure compliance without bureaucracy, making quality a strategic business advantage.
                    </p>
                    <div class="expertise-tags">
                        <span class="tag">Test Methods</span>
                        <span class="tag">Training</span>
                        <span class="tag">Standards</span>
                        <span class="tag">Process Design</span>
                        <span class="tag">Coaching</span>
                    </div>
                </div>
                <div class="expertise-card">
                    <div class="expertise-icon">👥</div>
                    <h3>Agile Leadership</h3>
                    <p>
                        Certified Scrum Master with extensive experience leading distributed teams and implementing Agile methodologies (Scrum, Kanban, SAFe). Building high-performing teams through mentorship, clear objectives, and collaborative culture.
                    </p>
                    <div class="expertise-tags">
                        <span class="tag">Scrum Master</span>
                        <span class="tag">Agile</span>
                        <span class="tag">SAFe</span>
                        <span class="tag">Team Building</span>
                        <span class="tag">Mentorship</span>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Experience Section -->
    <section id="experience" class="experience-section">
        <div class="container">
            <div class="section-header">
                <div class="section-subtitle">Career Journey</div>
                <h2>Professional Experience</h2>
                <p class="section-description">
                    19+ years of progressive leadership in quality engineering, spanning multiple industries and geographies
                </p>
            </div>
            <div class="timeline">
                <div class="timeline-item">
                    <div class="timeline-marker"></div>
                    <div class="timeline-content">
                        <div class="timeline-header">
                            <div class="timeline-company">Scania Group</div>
                            <div class="timeline-role">Method Developer</div>
                            <div class="timeline-period">January 2026 - Present | Stockholm, Sweden</div>
                        </div>
                        <div class="timeline-description">
                            Developing test methods and strategies ensuring functional safety and cybersecurity compliance. Creating frameworks that enable teams to build quality into products while adhering to ISO-26262 and ISO-21434 standards. Establishing best practices for safety-critical automotive systems.
                        </div>
                    </div>
                </div>

                <div class="timeline-item">
                    <div class="timeline-marker"></div>
                    <div class="timeline-content">
                        <div class="timeline-header">
                            <div class="timeline-company">Volvo Cars</div>
                            <div class="timeline-role">Test Architect</div>
                            <div class="timeline-period">August 2024 - December 2025 | Gothenburg, Sweden</div>
                        </div>
                        <div class="timeline-description">
                            Architected comprehensive test strategies for AD/ADAS system test team. Designed frameworks using pytest ensuring quality and safety for autonomous driving features. Led architecture decisions for test automation infrastructure.
                        </div>
                    </div>
                </div>

                <div class="timeline-item">
                    <div class="timeline-marker"></div>
                    <div class="timeline-content">
                        <div class="timeline-header">
                            <div class="timeline-company">Scania Group</div>
                            <div class="timeline-role">Test Coach</div>
                            <div class="timeline-period">November 2022 - June 2024 | Stockholm, Sweden</div>
                        </div>
                        <div class="timeline-description">
                            Coached organization on test strategies, methods, and tools for ISO-26262 functional safety and R155/R156 cybersecurity compliance. Created test methods, prepared training materials, and led teams through cybersecurity audits. Served as Scrum Master for coaching team.
                        </div>
                    </div>
                </div>

                <div class="timeline-item">
                    <div class="timeline-marker"></div>
                    <div class="timeline-content">
                        <div class="timeline-header">
                            <div class="timeline-company">ZF Group</div>
                            <div class="timeline-role">Engineering Leader</div>
                            <div class="timeline-period">October 2020 - October 2022 | Bengaluru, India</div>
                        </div>
                        <div class="timeline-description">
                            Led QA for fleet management telematics products. Managed 8 product lines with team of 8, while serving as Scrum Master for 20-member team. Established Bangalore operations from ground up. Drove improvements in navigation solutions and field test efficiency.
                        </div>
                    </div>
                </div>

                <div class="timeline-item">
                    <div class="timeline-marker"></div>
                    <div class="timeline-content">
                        <div class="timeline-header">
                            <div class="timeline-company">Leading Technology Firms</div>
                            <div class="timeline-role">Senior Leadership Roles</div>
                            <div class="timeline-period">2005 - 2020 | India, Ireland, Canada</div>
                        </div>
                        <div class="timeline-description">
                            Progressive career growth through HCL Technologies, Capgemini, Dassault Systèmes 3DEXCITE, and Wipro. Delivered complex projects for Ford Automotive, Bell Canada, Ericsson, and Cisco Systems. Built automation frameworks, led distributed teams, and implemented CI/CD solutions across telecom, automotive, and enterprise domains.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Leadership Section -->
    <section id="leadership">
        <div class="container">
            <div class="section-header">
                <div class="section-subtitle">Core Strengths</div>
                <h2>Leadership Approach</h2>
                <p class="section-description">
                    Building excellence through systematic thinking, strategic vision, and team empowerment
                </p>
            </div>
            <div class="leadership-grid">
                <div class="leadership-card">
                    <div class="leadership-icon">🎯</div>
                    <h3>Strategic Vision</h3>
                    <p>
                        Translating business objectives into actionable quality strategies that drive competitive advantage and ensure long-term sustainability.
                    </p>
                </div>
                <div class="leadership-card">
                    <div class="leadership-icon">🏛️</div>
                    <h3>Systematic Thinking</h3>
                    <p>
                        Applying rigorous methodologies and frameworks to create predictable, repeatable excellence in complex environments.
                    </p>
                </div>
                <div class="leadership-card">
                    <div class="leadership-icon">👥</div>
                    <h3>Team Empowerment</h3>
                    <p>
                        Building high-performing teams through mentorship, clear goals, and creating environments where people can excel.
                    </p>
                </div>
                <div class="leadership-card">
                    <div class="leadership-icon">🔒</div>
                    <h3>Compliance Excellence</h3>
                    <p>
                        Making regulatory compliance achievable through practical frameworks that integrate seamlessly with development processes.
                    </p>
                </div>
                <div class="leadership-card">
                    <div class="leadership-icon">💡</div>
                    <h3>Innovation Mindset</h3>
                    <p>
                        Pioneering new approaches to quality engineering through research, experimentation, and continuous improvement.
                    </p>
                </div>
                <div class="leadership-card">
                    <div class="leadership-icon">📊</div>
                    <h3>Results Focus</h3>
                    <p>
                        Delivering measurable business impact through quality initiatives that reduce costs and accelerate time-to-market.
                    </p>
                </div>
            </div>

            <div style="margin-top: 4rem;">
                <div class="section-header">
                    <div class="section-subtitle">Professional Credentials</div>
                    <h2>Certifications</h2>
                </div>
                <div class="cert-grid">
                    <div class="cert-card">
                        <div class="cert-badge">🏆</div>
                        <div class="cert-name">Professional Scrum Master I</div>
                        <div class="cert-issuer">Scrum.org</div>
                    </div>
                    <div class="cert-card">
                        <div class="cert-badge">✓</div>
                        <div class="cert-name">Certified Scrum Master</div>
                        <div class="cert-issuer">Scrum Alliance</div>
                    </div>
                    <div class="cert-card">
                        <div class="cert-badge">📋</div>
                        <div class="cert-name">ISTQB Foundation</div>
                        <div class="cert-issuer">ISTQB® International</div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="contact-section">
        <div class="container">
            <div class="contact-content">
                <div class="section-subtitle">Let's Connect</div>
                <h2>Ready to Transform Quality?</h2>
                <p class="section-description">
                    Whether you need strategic guidance on test architecture, functional safety expertise, or want to explore partnerships with QayiLabs and Mviaso—I'm here to help.
                </p>
                <div class="contact-info">
                    <div class="contact-item">
                        <div class="contact-item-icon"><i class="far fa-envelope"></i></div>
                        <!--<h4>Email</h4>-->
                        <a href="mailto:ahtnamus@gmail.com" title="Email">Email me</a>
                    </div>
                    <div class="contact-item">
                        <div class="contact-item-icon"><i class="fab fa-linkedin-in"></i></div>
                        <!--<h4>LinkedIn</h4>-->
                        <a href="https://www.linkedin.com/in/ahtnamus/" target="_blank">Connect with me</a>
                    </div>
<!-- For QayiLabs -->
<div class="contact-item">
    <div class="contact-item-icon">
        <img src="../assets/images/qayilabs-logo.png" alt="QayiLabs Logo" class="keep-color">
    </div>
    <!--<h4>QayiLabs</h4>-->
    <a href="https://qayilabs.com/" target="_blank">Visit QayiLabs</a>
</div>

<!-- For Mviaso -->
<div class="contact-item">
    <div class="contact-item-icon">
        <img src="../assets/images/mviaso-logo.png" alt="Mviaso Logo" class="keep-color">
    </div>
    <!--<h4>Mviaso Technologies</h4>-->
    <a href="https://mviaso.com/" target="_blank">Visit Mviaso</a>
</div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
  name: "HeroSection",
};
</script>

<style scoped>
.contact-item-icon {
    width: 64px;           /* Fixed width */
    height: 64px;          /* Fixed height */
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto 1rem;   /* Center it */
}

.contact-item-icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: brightness(0) invert(1);  /* Makes logos white */
}

/* If logos need to keep their colors on white background */
.contact-item-icon img.keep-color {
    filter: none;
    background: white;
    border-radius: 8px;
    padding: 8px;
}
</style>
